<template>
  <Card v-if="shouldShow" size="small" id="pd-access-instructions">
    <View gap="md" align="center">
      <IconTile icon="key" size="small" colored-icon :color="statusColor" />

      <View direction="column" gap="xs" class="content">
        <View gap="sm" align="center">
          <Text variant="label-medium">Access instructions</Text>
          <Badge v-if="accessMethod" variant="grey" :label="accessMethod" size="small" />
          <Badge v-if="hasInstruction && !isCodebox" :variant="badgeVariant" :label="badgeLabel" size="small" />
        </View>

        <Text v-if="isCodebox" variant="body-medium" color="tertiary">
          Tallo generates a fresh code for every showing and sends it to the renter automatically.
        </Text>
        <Text v-else-if="hasInstruction" variant="body-medium" color="tertiary" class="instruction-text">
          {{ instruction!.instructions }}
        </Text>
        <Text v-else variant="body-medium" color="tertiary">
          Add instructions so renters know how to access the property for self-guided showings
        </Text>
      </View>

      <Button variant="ghost" size="small" data-manage-action @click="openModal">
        <span>{{ accessMethod ? 'Edit' : 'Add' }}</span>
      </Button>
    </View>

    <EditAccessInstructionsModal ref="modalRef" :instruction="instruction" :property-id="propertyId" />
  </Card>
</template>

<script setup lang="ts">
import { format } from 'date-fns';
import { computed, ref, nextTick } from 'vue';
import { useRoute } from 'vue-router';

import { Badge, Button, Card, IconTile, Text, View } from '@tallo/design-system';
import { EditAccessInstructionsModal } from '@tallo/investor/intelligent-escalations';
import { usePropertyAccessInstructionsQuery, usePropertyStore } from '@tallo/investor/property';
import { SelfGuidedAccessMethod } from '@tallo/property';

const route = useRoute();
const propertyId = computed(() => route.params.propertyId as string);

const propertyStore = usePropertyStore();
const { data } = usePropertyAccessInstructionsQuery();
const modalRef = ref<InstanceType<typeof EditAccessInstructionsModal> | null>(null);

const instruction = computed(() => data.value || null);
const hasInstruction = computed(() => !!instruction.value);

const accessMethod = computed(() => propertyStore.property?.selfGuidedAccessMethod ?? null);
const isCodebox = computed(() => accessMethod.value === SelfGuidedAccessMethod.CODEBOX);

const shouldShow = computed(() => propertyStore.property?.allowsSelfGuidedTours === true);

const isInvalid = computed(() => {
  if (!instruction.value) return false;
  return instruction.value.isValid === false;
});

const isExpired = computed(() => {
  if (!instruction.value) return true;
  if (!instruction.value.expiresAt) return false;
  return new Date(instruction.value.expiresAt) < new Date();
});

const statusColor = computed(() => {
  if (isCodebox.value) return 'green';
  // No instruction yet → a warm "add me" call-to-action; otherwise reflect validity.
  if (!hasInstruction.value) return 'orange';
  if (isInvalid.value) return 'red';
  return isExpired.value ? 'red' : 'green';
});

const badgeVariant = computed(() => {
  if (isInvalid.value) return 'red';
  if (isExpired.value) return 'red';
  return 'green';
});

const badgeLabel = computed(() => {
  if (isInvalid.value) return 'Invalid — reported by renter';
  if (isExpired.value) return 'Expired';
  if (!instruction.value?.expiresAt) return 'Active';
  return `Active until ${formattedExpiry.value}`;
});

const formattedExpiry = computed(() => {
  if (!instruction.value?.expiresAt) return '';
  return format(new Date(instruction.value.expiresAt), 'MMM d, yyyy');
});

function openModal() {
  nextTick(() => {
    modalRef.value?.open();
  });
}
</script>

<style scoped lang="scss">
.content {
  flex: 1;
}

.instruction-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
